import Fade from '@rio-cloud/rio-uikit/Fade';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { TourStopDetails } from '#components/tourstopdetails/TourStopDetails.tsx';
import { getTrackingAttributes, TRACKING_ACTIONS, TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import { useAppDispatch, useAppSelector } from '#configuration/setup/hooks.ts';
import type { Driver } from '#domain/model/Driver.ts';
import type { Tour } from '#domain/model/Tour.ts';
import { WidgetTourDetails } from '#features/livemonitor/WidgetTourDetails.tsx';
import { useTrackScreenSeen } from '#hooks/useTrackScreenSeen.ts';
import { mapMarkerSelected, selectSelectedMapMarkerId } from '#store/widgetSlice.ts';

import { config } from '../../config';

type WidgetTourDetailsProps = {
    selectedTour: Tour;
    driver?: Driver;
    canOpenTourInOrderCommunication?: boolean;
};

export const WidgetTourView = ({
    selectedTour,
    driver,
    canOpenTourInOrderCommunication = false,
}: WidgetTourDetailsProps) => {
    const ref = useRef(null);
    const onScreen = useOnScreen(ref);
    const dispatch = useAppDispatch();
    const selectedMapMarkerId = useAppSelector(selectSelectedMapMarkerId);
    const [selectedStopId, setSelectedStopId] = useState<string>();

    useTrackScreenSeen(onScreen, {
        category: TRACKING_CATEGORIES.LIVEMONITOR,
        action: TRACKING_ACTIONS.ON_SCREEN,
        label: 'Show livemonitor tour widget',
    });

    useEffect(() => {
        if (selectedMapMarkerId) {
            setSelectedStopId(selectedMapMarkerId);
        }
    }, [selectedMapMarkerId]);

    useEffect(() => {
        if (!selectedStopId) {
            return;
        }

        if (!selectedTour.stops.some(stop => stop.id === selectedStopId)) {
            setSelectedStopId(undefined);
        }
    }, [selectedStopId, selectedTour]);

    const selectedStop = selectedTour.stops.find(stop => stop.id === selectedStopId);
    const hasSelectedStop = !!selectedStopId && !!selectedStop;
    const animationStyle = hasSelectedStop ? 'page' : 'pageBack';
    const titleHref = canOpenTourInOrderCommunication
        ? `${config.orderCommunicationFmsUrl}/#/tours/${selectedTour.id}`
        : undefined;

    return (
        <div
            className={classNames({ 'widget-dev-styles': import.meta.env.DEV })}
            ref={ref}
            {...getTrackingAttributes({
                trigger: 'visibility',
                category: TRACKING_CATEGORIES.LIVEMONITOR,
                action: TRACKING_ACTIONS.RENDER,
                label: 'Render livemonitor tour widget',
                value: '',
            })}
        >
            <Fade
                initial={false}
                exitBeforeEnter
                animationStyle={animationStyle}
                duration={0.25}
                motionProps={{ transition: { duration: 0.25, type: 'spring' } }}
            >
                {!hasSelectedStop && (
                    <div className='padding-x-15'>
                        <WidgetTourDetails
                            key='tour-details'
                            title={selectedTour.searchName}
                            href={titleHref}
                            tour={selectedTour}
                            driver={driver}
                            setSelectedStopId={setSelectedStopId}
                        />
                    </div>
                )}
                {hasSelectedStop && selectedStop && (
                    <TourStopDetails
                        title={selectedStop.name}
                        key='tour-stop-details'
                        onBack={() => {
                            dispatch(mapMarkerSelected(undefined));
                            setSelectedStopId(undefined);
                        }}
                        stop={selectedStop}
                        transportOrders={selectedTour.transportOrders.filter(order =>
                            selectedStop.transportOrderIds.includes(order.id)
                        )}
                        serviceOrders={selectedTour.serviceOrders.filter(order =>
                            selectedStop.serviceOrderIds.includes(order.id)
                        )}
                        type='widget'
                    />
                )}
            </Fade>
        </div>
    );
};
